import { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { reduxTypedHooks, userSharedSelectors } from 'common/modules/ReactShared';
import { t, useLocale } from 'i18n';
import { getCountryFlag } from 'i18n/config/countryFlags';
import { getCountryName } from 'i18n/countries';

const PERSONAL_INFORMATION_PATH = '/settings/account';

interface INationalityNote {
  /** Lets the menu close itself when the student follows the link out of it. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * What the student's flag actually says about them, shown where they come to change
 * their support language.
 *
 * The flag beside a student used to mean "studies through this language", and this menu
 * is where that language is chosen — so it is where someone wonders why their badge
 * shows a country. Naming the nationality here, with the way to change it, answers that
 * question in the place it gets asked.
 */
const NationalityNote: FC<INationalityNote> = ({ onNavigate, className }) => {
  const profile = reduxTypedHooks.useTypedSelector(userSharedSelectors.selectProfile);
  const { localeCode } = useLocale();

  const nationality = profile?.nationality ?? '';
  const countryName = getCountryName(nationality, localeCode);

  return (
    <SNote className={className}>
      <SLabel>
        {t('frontend.member.lang_dropdown.nationality', { defaultValue: 'Nationality' })}
      </SLabel>

      <SValue>
        {countryName ? (
          <>
            <SFlag src={getCountryFlag(nationality)} alt="" />
            <span>{countryName}</span>
          </>
        ) : (
          <SUnset>
            {t('frontend.member.lang_dropdown.nationality_unset', {
              defaultValue: 'Not stated'
            })}
          </SUnset>
        )}
      </SValue>

      <SExplanation>
        {countryName
          ? t('frontend.member.lang_dropdown.nationality_shown', {
              defaultValue: 'This flag stands for you instead of your support language.'
            })
          : t('frontend.member.lang_dropdown.nationality_absent', {
              defaultValue: 'Your support-language flag stands for you until you set one.'
            })}
      </SExplanation>

      <SLink to={PERSONAL_INFORMATION_PATH} onClick={onNavigate}>
        {countryName
          ? t('frontend.member.lang_dropdown.nationality_change', {
              defaultValue: 'Change nationality'
            })
          : t('frontend.member.lang_dropdown.nationality_add', {
              defaultValue: 'Add your nationality'
            })}
      </SLink>
    </SNote>
  );
};

export default NationalityNote;

const SNote = styled.div`
  margin-block-start: 12px;
  padding-block-start: 12px;
  border-top: 1px solid #e6e6f0;
  white-space: normal;
  max-width: 260px;
`;

const SLabel = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-black);
`;

const SValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-block-start: 4px;
  font-size: 1rem;
  color: var(--color-black);
`;

const SFlag = styled.img`
  width: 23px;
  height: 17px;
  flex-shrink: 0;
`;

/* --color-grey is 2.29:1 on this white menu. The app's own dark ink carries the value
   at 13.6:1, italic rather than grey to mark it as an absence. */
const SUnset = styled.span`
  font-style: italic;
  color: var(--color-black);
`;

const SExplanation = styled.p`
  margin: 4px 0 0;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-gray-900);
`;

/* Same accessible blue as the "add another language" button below it: --color-blue is
   1.57:1 on white. */
const SLink = styled(Link)`
  display: inline-block;
  margin-block-start: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-blue-1000);
  text-decoration: underline;

  &:focus-visible {
    outline: 3px solid var(--color-blue-btn-focus-outline-color);
    outline-offset: 3px;
    border-radius: 3px;
  }
`;
